<script setup lang="ts">
const id = extractId(useRoute().params.id as string)
if (!id) {
  throw createError({ statusCode: 404, statusMessage: `Page not found: ${useRoute().fullPath}` })
}
const { $api } = useNuxtApp()

interface BoatPolarData {
  data: Record<string, Record<string, { target_speed_ms: number, samples: number }>>,
  sample_activity_count: number,
  computed_at: string,
}

const { data, error } = await useAsyncData(`boat-${id}`, async () : Promise<{ boat: Boat, activityCount: number, competitions: Array<Competition>, polar: BoatPolarData | null }> => {
  const [boat, activities, competitionsRes, polar] = await Promise.all([
    $api(`boats/${id}/`),
    $api(`/performance/activities/?ordering=-start&upload__boat__id=${id}&page_size=1`),
    $api(`/competitions/competitions/?boat=${id}&page_size=6&ordering=-start_date`),
    // 404 = no polar generated yet for this boat -- not an error, just nothing to show
    $api(`boats/${id}/polar/`).catch(() => null),
  ])
  return { boat, activityCount: activities.count, competitions: competitionsRes.results, polar }
})

if (error.value) {
  throw createError({ statusCode: error.value.statusCode ?? 404, statusMessage: `Page not found: ${useRoute().fullPath}` })
}

const boat = data.value?.boat as Boat
const activityCount = data.value?.activityCount ?? 0
const competitions = data.value?.competitions as Array<Competition>
const polar = data.value?.polar ?? null

// A boat-level polar is only meaningful once at least one wind-speed-bucketed curve exists --
// pooled activities that never got a wind speed estimate land in a catch-all "unknown" bucket
// (see performance/worker.py::generate_boat_polar), which isn't a usable "target speed by wind
// strength" reference. Treat that the same as no polar generated yet.
const hasWindSpeedPolar = computed(() => !!polar && Object.keys(polar.data).some(key => key !== 'unknown'))

const polarComputedAtLabel = computed(() => {
  if (!polar?.computed_at) return null
  return new Date(polar.computed_at).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })
})

// Classes with no recorded activity yet are thin/empty pages — keep them
// out of search results and off direct links, same gating as the /boats
// listing and the boats sitemap.
if (!activityCount) {
  throw createError({ statusCode: 404, statusMessage: `Page not found: ${useRoute().fullPath}` })
}

const categoryLabel: Record<string, string> = { DI: 'Dinghy', KB: 'Keelboat', OT: 'Other' }

const seoDescription = hasWindSpeedPolar.value
  ? `${boat.name} polar diagram: target boat speed by wind angle, pooled from ${polar!.sample_activity_count} community sailing sessions. Plus recent GPS-tracked tracks and competitions for the ${boat.name} on SailingMetrics.`
  : `Recent GPS-tracked sailing sessions and competitions for the ${boat.name}. Browse tracks, race results and regattas sailed in a ${boat.name} on SailingMetrics.`

useSeoMeta({
  title: `${boat.name} Sailing Boat Class`,
  description: seoDescription,
  ogTitle: `${boat.name} Sailing Boat Class`,
  ogDescription: seoDescription,
  ogType: 'website',
})

useHead({
  script: [{
    type: 'application/ld+json',
    innerHTML: JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://sailingmetrics.com/' },
        { '@type': 'ListItem', position: 2, name: 'Boat classes', item: 'https://sailingmetrics.com/boats' },
        { '@type': 'ListItem', position: 3, name: boat.name, item: `https://sailingmetrics.com/boats/${boat.slug}-${boat.id}` },
      ],
    }),
  }],
})

const breadcrumbs = [
  { title: 'Home', disabled: false, href: '/' },
  { title: 'Boat classes', disabled: false, href: '/boats' },
  { title: boat.name, disabled: true, href: `/boats/${boat.slug}-${boat.id}` },
]
</script>
<template>
  <v-container>
    <v-row>
      <v-col>
        <Breadcrumbs :items="breadcrumbs" />
        <div class="d-flex align-center flex-wrap ga-3">
          <h1>{{ boat.name }}</h1>
          <v-chip v-if="boat.category" size="small" variant="tonal" color="primary">
            {{ categoryLabel[boat.category] || boat.get_category_display }}
          </v-chip>
        </div>
      </v-col>
    </v-row>

    <v-row>
      <v-col>
        <h2>{{ boat.name }} polar diagram</h2>
      </v-col>
    </v-row>

    <v-row>
      <v-col :cols="12" :md="hasWindSpeedPolar ? 6 : 12">
        <template v-if="hasWindSpeedPolar">
          <p>
            A polar diagram shows how fast a {{ boat.name }} typically sails at each angle to the
            wind, giving you target speeds to steer to. This one is built from GPS tracks recorded
            by the SailingMetrics community sailing a {{ boat.name }} — the more tracks, the more
            accurate it gets.
          </p>
          <p class="text-body-2 text-medium-emphasis">
            Target boat speed by angle to the wind (0° = head to wind, 180° = dead downwind), one
            curve per wind strength -- lighter blue for light wind, darker blue for stronger wind.
            Click a wind-speed entry in the chart's legend to isolate that curve. Pooled from
            <template v-if="polar!.sample_activity_count">{{ polar!.sample_activity_count }} community sailing sessions</template>
            <template v-else>community sailing sessions</template>
            on this boat<template v-if="polarComputedAtLabel">, last updated {{ polarComputedAtLabel }}</template>.
          </p>
        </template>
        <p v-else>
          A polar diagram shows how fast a {{ boat.name }} typically sails at each angle to the
          wind, pooled from GPS tracks recorded by the SailingMetrics community. No polar diagram
          has been generated yet for the {{ boat.name }} — upload a track and be the first to
          contribute.
        </p>
        <v-btn color="primary" prepend-icon="mdi-upload" :to="`/upload?boat=${boat.id}`" nuxt class="mt-2">
          Upload your GPS track
        </v-btn>
      </v-col>

      <v-col v-if="hasWindSpeedPolar" cols="12" md="6">
        <BoatPolarChart :polar="polar!.data" />
      </v-col>
    </v-row>

    <v-row>
      <v-col>
        <h2>Tracks with the {{ boat.name }}</h2>
        <p>
          The following sailing activities with the {{ boat.name }} were recently recorded:
        </p>
        <ActivityTable :boat-id="boat.id" :show-boat="false" />
      </v-col>
    </v-row>

    <v-row v-if="competitions.length">
      <v-col>
        <h2>Competitions using the {{ boat.name }}</h2>
        <v-list lines="two">
          <v-list-item
            v-for="c in competitions"
            :key="c.id"
            :to="{ name: 'competitions-competition___en', params: { competition: `${c.slug}-${c.id}` } }"
            nuxt
            :title="c.name"
            :subtitle="[c.venue_details?.name || c.venue_name, c.start_date].filter(Boolean).join(' · ')"
          />
        </v-list>
      </v-col>
    </v-row>
  </v-container>
</template>
